import React, { useState, useEffect, useCallback, useRef } from "react";

/* ------------- Inline Icons (replaces lucide-react) ------------- */

const IconBase = ({ children, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {children}
  </svg>
);

const RotateCcw = (props) => (
  <IconBase {...props}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </IconBase>
);

const RefreshCw = (props) => (
  <IconBase {...props}>
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M3 21v-5h5" />
  </IconBase>
);

const Info = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </IconBase>
);

const CheckCircle2 = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </IconBase>
);

const AlertCircle = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </IconBase>
);

const MousePointer2 = (props) => (
  <IconBase {...props}>
    <path d="m12 12 5.83 2.91-2.91 5.83" />
    <path d="M11.9 12.1 3 3l9 18 2.9-5.9 5.9-2.9-18-9Z" />
  </IconBase>
);

/* ------------- DAG Generation ------------- */

const generateDAG = (nodeCount = 6) => {
  const nodes = [];
  const width = 800;
  const height = 500;

  for (let i = 0; i < nodeCount; i++) {
    nodes.push({
      id: i,
      label: String.fromCharCode(65 + i),
      x: 100 + Math.random() * (width - 200),
      y: 50 + Math.random() * (height - 100),
    });
  }

  const edges = [];
  const shuffled = [...Array(nodeCount).keys()].sort(() => Math.random() - 0.5);
  const rank = {};
  shuffled.forEach((id, r) => (rank[id] = r));

  for (let i = 0; i < nodeCount; i++) {
    for (let j = 0; j < nodeCount; j++) {
      if (i === j) continue;
      if (rank[i] < rank[j] && Math.random() > 0.65) {
        edges.push({ source: i, target: j });
      }
    }
  }

  if (edges.length < nodeCount - 1) {
    for (let k = 0; k < nodeCount - 1; k++) {
      const s = shuffled[k];
      const t = shuffled[k + 1];
      if (!edges.find((e) => e.source === s && e.target === t))
        edges.push({ source: s, target: t });
    }
  }

  return { nodes, edges };
};

/* ------------- MAIN VISUAL COMPONENT ------------- */

export default function ZeroIndegreeVisual() {
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [processed, setProcessed] = useState([]);
  const [indeg, setIndeg] = useState({});
  const [message, setMessage] = useState({
    text: "Welcome! Click 'New Graph' to start.",
    type: "neutral",
  });

  const [dragging, setDragging] = useState(null);
  const svgRef = useRef(null);

  const computeIndegrees = useCallback(
    (edges, removed) => {
      const counts = {};
      graph.nodes.forEach((n) => (counts[n.id] = 0));

      edges.forEach((e) => {
        if (!removed.includes(e.source)) {
          counts[e.target] = (counts[e.target] || 0) + 1;
        }
      });

      return counts;
    },
    [graph.nodes]
  );

  const newGraph = () => {
    const g = generateDAG(5 + Math.floor(Math.random() * 3));
    setGraph(g);
    setProcessed([]);
    const initial = {};
    g.nodes.forEach((n) => (initial[n.id] = 0));
    g.edges.forEach((e) => (initial[e.target] += 1));
    setIndeg(initial);
    setMessage({ text: "Select a node with In-Degree 0.", type: "info" });
  };

  useEffect(() => {
    newGraph();
  }, []);

  const handleNodeClick = (id) => {
    if (processed.includes(id)) return;
    const d = indeg[id];

    if (d === 0) {
      const next = [...processed, id];
      setProcessed(next);
      const updated = computeIndegrees(graph.edges, next);
      setIndeg(updated);

      if (next.length === graph.nodes.length)
        setMessage({ text: "Topological Sort Complete!", type: "success" });
      else
        setMessage({
          text: `Removed ${graph.nodes.find((n) => n.id === id).label}.`,
          type: "success",
        });
    } else {
      setMessage({
        text: `Cannot remove: In-Degree = ${d}`,
        type: "error",
      });
    }
  };

  const handleMouseDown = (e, id) => {
    e.stopPropagation();
    setDragging(id);
  };

  const handleMouseMove = (e) => {
    if (dragging === null) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setGraph((prev) => ({
      ...prev,
      nodes: prev.nodes.map((n) =>
        n.id === dragging ? { ...n, x, y } : n
      ),
    }));
  };

  const handleMouseUp = () => setDragging(null);

  const nodeColor = (id) => {
    if (processed.includes(id))
      return "fill-gray-200 stroke-gray-300";
    if (indeg[id] === 0)
      return "fill-green-100 stroke-green-500 shadow";
    return "fill-white stroke-slate-300";
  };

  return (
    <div
      className="flex flex-col h-[800px] bg-slate-50"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* HEADER */}
      <header className="flex justify-between items-center p-4 bg-white border-b">
        <h1 className="text-xl font-bold text-slate-700 flex items-center gap-2">
          <RotateCcw /> Zero Indegree Algorithm
        </h1>

        <button
          onClick={newGraph}
          className="px-4 py-2 bg-slate-800 text-white rounded-md flex items-center gap-2"
        >
          <RefreshCw /> New Graph
        </button>
      </header>

      <div className="flex flex-1">
        {/* SIDEBAR */}
        <aside className="w-72 bg-white border-r p-4 flex flex-col">
          <h2 className="font-bold mb-3 flex items-center gap-2">
            <Info /> Algorithm Steps
          </h2>

          <ol className="text-sm ml-4 list-decimal text-slate-600 space-y-1">
            <li>Compute indegrees</li>
            <li>Select node with indegree 0</li>
            <li>Remove node</li>
            <li>Decrement neighbors</li>
            <li>Repeat</li>
          </ol>

          {/* MESSAGE */}
          <div
            className={
              "p-3 mt-4 rounded text-sm flex gap-2 " +
              (message.type === "error"
                ? "bg-red-50 text-red-700"
                : message.type === "success"
                ? "bg-green-50 text-green-700"
                : "bg-blue-50 text-blue-700")
            }
          >
            {message.type === "error" && <AlertCircle />}
            {message.type === "success" && <CheckCircle2 />}
            {message.type === "info" && <MousePointer2 />}
            {message.text}
          </div>

          <h3 className="mt-6 mb-2 text-xs font-bold text-slate-500 uppercase">
            Topological Order
          </h3>

          <div className="flex-1 overflow-y-auto space-y-2">
            {processed.map((id, i) => {
              const n = graph.nodes.find((n) => n.id === id);
              return (
                <div key={id} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-4">{i + 1}.</span>
                  <div className="w-8 h-8 bg-indigo-100 text-indigo-700 border border-indigo-300 rounded-full flex items-center justify-center font-bold">
                    {n.label}
                  </div>
                  <CheckCircle2 className="text-green-500" />
                </div>
              );
            })}
          </div>
        </aside>

        {/* MAIN GRAPH */}
        <main className="flex-1 relative">
          <svg ref={svgRef} className="w-full h-full">
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="28"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
                  </marker>
                  <marker
                    id="arrowhead-faded"
                    markerWidth="10"
                    markerHeight="7"
                    refX="28"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="#e2e8f0" />
                  </marker>
                </defs>
            {/* edges */}
            {graph.edges.map((e, i) => {
              const a = graph.nodes.find((n) => n.id === e.source);
              const b = graph.nodes.find((n) => n.id === e.target);
              return (
                <line
                  markerEnd="url(#arrowhead)"
                  key={i}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke={processed.includes(e.source) ? "#d1d5db" : "#94a3b8"}
                  strokeWidth={2}
                />
              );
            })}

            {/* nodes */}
            {graph.nodes.map((n) => (
              <g
                key={n.id}
                transform={`translate(${n.x},${n.y})`}
                onMouseDown={(e) => handleMouseDown(e, n.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNodeClick(n.id);
                }}
                style={{ cursor: "pointer" }}
              >
                <circle
                  r={20}
                  className={`transition-all duration-200 ${nodeColor(n.id)}`}
                />
                <text
                  dy=".3em"
                  textAnchor="middle"
                  className="font-bold text-slate-800 pointer-events-none select-none"
                >
                  {n.label}
                </text>

                {!processed.includes(n.id) && (
                  <g transform="translate(14,-14)">
                    <circle
                      r={10}
                      className={
                        indeg[n.id] === 0
                          ? "fill-green-600 animate-pulse"
                          : "fill-slate-500"
                      }
                    />
                    <text
                      dy=".3em"
                      textAnchor="middle"
                      className="text-white text-[10px] font-bold"
                    >
                      {indeg[n.id]}
                    </text>
                  </g>
                )}
              </g>
            ))}
          </svg>
        </main>
      </div>
    </div>
  );
}
